import React, { useState, useEffect } from "react";
import { getCategories } from "../apiConfig/services";
import { useNavigate } from "react-router-dom";
import CardCategoryShimmer from "./loading/CardCategoryShimmer";

function CardCategory() {
  const [cardData, setCardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const colors = [
    "rgba(158, 173, 254, 0.2)",
    "rgba(70, 207, 199, 0.2)",
    "rgba(254, 175, 158, 0.2)",
    "rgba(100, 181, 246, 0.2)",
    "rgba(254, 175, 158, 0.2)",
    "rgba(38, 166, 154, 0.2)",
    "rgba(158, 173, 254, 0.2)",
    "rgba(70, 207, 199, 0.2)",
    "rgba(254, 175, 158, 0.2)",
  ];

  // Mappage des descriptions courtes par slug de catégorie
  const shortDescriptions = {
    "cours-r-dig-s":
      "Cours télédétection Radar par Jean Paul RUDANT, Manuel rédigé en 2023 et consacré à l'imagerie radar",
    "plans-resum-s-et-liens-vers-les-cours-radar-optique-radar-lidar-de-l-ensg":
      "Il s'agit des cours vidéo disponibles sur le site ENSG. L'ordre de consultation le plus pratique pour ces cours, est TeledetectionRadar ComparaisonOptiqueRadar TeledetectionLidar",
    "mooc-multilingue-et-pdf-avec-liens-actifs-eocoll-ge-de-l-universit-jena-allemagne-":
      "Le MOOC présente les connaissances essentielles sur l'imagerie radar, 4 langues anglais, français, allemand, espagnol. Un fichier pdf avec liens actifs vers des vidéos est proposé en complément",
    "revue-rfpt":
      "La revue RFPT (Revue Française de Photogrammétrie et Télédétection) permet un accès libre et gratuit aux articles, récents ou d'archive. Plusieurs numéros spéciaux sont accessibles, en particulier sur les sujets AFRIQUE et RADAR.",
    "power-point-annot-":
      "Master IASIG DOUALA Radar POWER POINT annoté, année 2014. Il contient de nombreux développements calculatoires pouvant être considérés comme des exercices.",
    "expos-s-power-point-ecole-d-t-univ-fiana-avril-2024":
      "Trois exposés ont eu lieu les 24, 25, 26 avril 2024 lors de l'Ecole d'été de l'université de Fianarantsoa: Imagerie Radar",
    "plan-r-sum-et-liens-vers-le-cours-radar-de-l-esa-sous-titrage-anglais-et-espagnol":
      "Ce plan résumé du cours présent sur le site ESA permet de retrouver les paragraphes successifs selon l'intérêt du lecteur.",
    "documentaires-generaux":
      "Il s'agit de plusieurs documentaires portant sur la télédétection (RADAR, OPTIQUE, LIDAR)",
    "ressource-en-malagasy":
      "Il s'agit du film « Teledetection radar CNRS de 1987» sous titré en malagasy",
  };

  const fetchCategoryAndCount = async () => {
    try {
      setLoading(true);
      const categories = await getCategories();
  
      // Ordre défini par les slugs
      const order = [
        "cours-r-dig-s",
        "documentaires-generaux",
        "ressource-en-malagasy",
        "plan-r-sum-et-liens-vers-le-cours-radar-de-l-esa-sous-titrage-anglais-et-espagnol",
        "plans-resum-s-et-liens-vers-les-cours-radar-optique-radar-lidar-de-l-ensg",
        "power-point-annot-",
        "mooc-multilingue-et-pdf-avec-liens-actifs-eocoll-ge-de-l-universit-jena-allemagne-",
        "expos-s-power-point-ecole-d-t-univ-fiana-avril-2024",
        "revue-rfpt",
      ];
  
      // Réordonner selon la liste ci-dessus
      const sortedCategories = categories.sort(
        (a, b) => order.indexOf(a.slug_category) - order.indexOf(b.slug_category)
      );
  
      const mergedData = sortedCategories.map((cat, index) => ({
        ...cat,
        bgurl: true,
        couleur: colors[index % colors.length],
        short_desc_category:
          shortDescriptions[cat.slug_category] || cat.desc_category,
      }));
  
      setCardData(mergedData);
    } catch (error) {
      console.error("Erreur lors de la récupération des catégories", error);
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    fetchCategoryAndCount();
  }, []);

  const handleNavigationPage = (categoryNomDomaine) => () => {
    const encodedCategory = encodeURIComponent(categoryNomDomaine);
    navigate(`/categories/${encodedCategory}`);
    window.scrollTo(0, 0); // Ajoute cette ligne pour scroll en haut après navigation
  };

  if (loading) {
    return <CardCategoryShimmer />;
  }

  return (
    <div className="mt-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {cardData.map((card) => (
          <div
            key={card.id_category}
            className={`relative w-full h-48 lg:h-96 rounded-2xl overflow-hidden  group cursor-pointer hover:-translate-y-1 ${
              card.bgurl ? "card-categorie-bgimage" : ""
            }`}
            onClick={handleNavigationPage(card.slug_category)}
          >
            {/* Overlay de couleur */}
            <div
              className="absolute inset-0 z-10"
              style={{ backgroundColor: card.couleur }}
            ></div>

            {/* Contenu de la carte */}
            <div className="absolute inset-0 z-20 p-5 flex flex-col justify-center text-center text-slate-700">
              <h2 className="text-lg lg:text-xl font-bold mb-3 leading-tight drop-shadow-lg">
                {card.name_category}
              </h2>
              <p className="text-sm lg:text-base opacity-95 leading-relaxed drop-shadow-md">
                {card.short_desc_category}
              </p>
            </div>

            {/* Flèche de navigation */}
            <div className="absolute bottom-4 right-4 z-30 text-slate-700 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">
              <svg
                className="w-5 h-5 lg:w-6 lg:h-6"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CardCategory;
